"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAllInstalledIntegrations,
  InstalledIntegration_ALL_GET,
} from "@/api/client/installedintegration";
import { getUserEmailSetting, USER_EMAIL_SETTING_KEY } from "@/api";
import {
  EMAIL_INTEGRATION_TYPES,
  InstalledIntegration,
  InstalledIntegrationType,
  MailProvider,
  MailProviderInfo,
} from "@/types/installedintegration";
import { useSession } from "@/hooks/useSession";

const MAIL_INTEGRATION_TYPES: InstalledIntegrationType[] = [
  InstalledIntegrationType.PARAGON_GOOGLE,
  InstalledIntegrationType.PARAGON_MICROSOFT,
  InstalledIntegrationType.GOOGLECALENDAR,
  InstalledIntegrationType.MICROSOFTCALENDAR,
];

const providerForType = (
  type: InstalledIntegrationType,
): MailProvider | null => {
  switch (type) {
    case InstalledIntegrationType.PARAGON_GOOGLE:
    case InstalledIntegrationType.GOOGLECALENDAR:
      return "google";
    case InstalledIntegrationType.PARAGON_MICROSOFT:
    case InstalledIntegrationType.MICROSOFTCALENDAR:
      return "microsoft";
    default:
      return null;
  }
};

const supportsEmailForType = (type: InstalledIntegrationType): boolean =>
  EMAIL_INTEGRATION_TYPES.includes(type);

const supportsCalendarForType = (type: InstalledIntegrationType): boolean =>
  type === InstalledIntegrationType.GOOGLECALENDAR ||
  type === InstalledIntegrationType.MICROSOFTCALENDAR;

const providerRank = (provider: MailProvider): number =>
  provider === "microsoft" ? 0 : 1;

const createdAtValue = (createdAt: string | number | undefined): number => {
  if (createdAt == null) return Number.POSITIVE_INFINITY;
  if (typeof createdAt === "number") return createdAt;
  const parsed = Date.parse(createdAt);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

interface MailCandidate {
  row: InstalledIntegration;
  provider: MailProvider;
  supportsEmail: boolean;
  supportsCalendar: boolean;
}

const toMailProviderInfo = (candidate: MailCandidate): MailProviderInfo => ({
  provider: candidate.provider,
  account: {
    integrationId: candidate.row.id,
    email: candidate.row.username ?? "",
    supportsCalendar: candidate.supportsCalendar,
    supportsEmail: candidate.supportsEmail,
  },
});

const emailsMatch = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

const pickMailProvider = (
  installedIntegrations: InstalledIntegration[] | undefined,
  userEmail: string | null | undefined,
  defaultEmailAccountId: string | null | undefined,
): MailProviderInfo | null => {
  if (!installedIntegrations?.length) return null;

  const candidates = installedIntegrations
    .filter((row) => MAIL_INTEGRATION_TYPES.includes(row.integration))
    .map((row) => {
      const provider = providerForType(row.integration);
      if (!provider) return null;
      return {
        row,
        provider,
        supportsEmail: supportsEmailForType(row.integration),
        supportsCalendar: supportsCalendarForType(row.integration),
      };
    })
    .filter((c): c is MailCandidate => c !== null);

  if (!candidates.length) return null;

  // (0) Highest priority: the account the user explicitly saved as their
  // default in Email settings, matched by InstalledIntegration id. If the saved
  // account no longer exists (e.g. it was disconnected), fall through to the
  // heuristics below.
  if (defaultEmailAccountId) {
    const savedDefault = candidates.find(
      (c) => c.row.id === defaultEmailAccountId,
    );
    if (savedDefault) return toMailProviderInfo(savedDefault);
  }

  // (1) An email-provider account whose login matches the signed-in user's
  // email. Only the email provider (not calendar) is considered here.
  const userEmailMatch = candidates.find(
    (c) => c.supportsEmail && emailsMatch(c.row.username, userEmail),
  );
  if (userEmailMatch) return toMailProviderInfo(userEmailMatch);

  candidates.sort((a, b) => {
    // (2) email-capable first
    if (a.supportsEmail !== b.supportsEmail) {
      return a.supportsEmail ? -1 : 1;
    }
    // (3) Microsoft before Google
    const providerDelta = providerRank(a.provider) - providerRank(b.provider);
    if (providerDelta !== 0) return providerDelta;
    // (4) earliest createdAt ascending
    return createdAtValue(a.row.createdAt) - createdAtValue(b.row.createdAt);
  });

  return toMailProviderInfo(candidates[0]);
};

export interface UseDraftEmailsResult {
  mailProvider: MailProviderInfo | null;
  installedIntegrations: InstalledIntegration[] | undefined;
  isLoading: boolean;
}

export const useDraftEmails = (): UseDraftEmailsResult => {
  const { user } = useSession();

  const { data: installedIntegrations, isLoading } = useQuery<
    InstalledIntegration[]
  >({
    queryKey: [InstalledIntegration_ALL_GET],
    queryFn: () => getAllInstalledIntegrations(),
  });

  // The user's saved default mailbox lives in their email setting. When it is
  // unset, `defaultEmailAccountId` is undefined and the resolution falls back
  // to the existing heuristics.
  const { data: emailSetting, isLoading: isSettingLoading } = useQuery({
    queryKey: [USER_EMAIL_SETTING_KEY],
    queryFn: getUserEmailSetting,
  });

  const mailProvider = useMemo(
    () =>
      pickMailProvider(
        installedIntegrations,
        user?.email,
        emailSetting?.defaultEmailAccountId,
      ),
    [installedIntegrations, user?.email, emailSetting?.defaultEmailAccountId],
  );

  return {
    mailProvider,
    installedIntegrations,
    isLoading: isLoading || isSettingLoading,
  };
};
